import { APIErrorEnum } from "@/types/network";
import { useCallback, useContext, useEffect, useRef } from "react";
import { useFetchInterceptor } from "./useErrorInterceptor";
import { AuthContext } from "react-oauth2-code-pkce";

export function useAuthRedirect() {
  const { token, logIn, logOut, loginInProgress } = useContext(AuthContext);
  const { error, clearError } = useFetchInterceptor();
  const logoutAttemptsRef = useRef(0);
  const loginAttemptsRef = useRef(0);

  const clearUser = useCallback(() => {
    // Clear user and replace with base URL and all necessary params
    const baseUrl = new URL(window.location.href).origin;
    const searchParams = new URLSearchParams(window.location.search);
    const hasSearchParams = searchParams.toString().length > 0;
    window.history.replaceState({}, document.title, `${baseUrl}${hasSearchParams ? "?" : ""}${searchParams.toString()}`);
  }, []);

  const login = useCallback(() => {
    if (loginAttemptsRef.current > 0) return;
    loginAttemptsRef.current = 1;
    clearError();
    clearUser();
    logIn(undefined, { returnUrl: window.location.origin });
  }, [logIn, clearError, clearUser]);

  const logout = useCallback(
    (returnUrl: string = window.location.origin) => {
      if (logoutAttemptsRef.current > 0) return;
      logoutAttemptsRef.current = 1;
      clearUser();
      clearError();

      logOut(undefined, undefined, { returnUrl });
    },
    [logOut, clearError, clearUser],
  );

  // invalid_grant auto-logout
  useEffect(() => {
    if (error?.status === 400 && error?.message === APIErrorEnum.INVALID_GRANT) {
      logout();
    }
    // Non-payer accounts are now rejected by the BE with 403 on any request
    if (error?.status === 403) {
      const redirectURL = import.meta.env.VITE_IBABS_PORTAL_URL || "https://portal.ibabs.eu";
      logout(`${redirectURL}/Error.htm`);
    }
  }, [error, logout]);

  useEffect(() => {
    if (!token && !loginInProgress) login();
  }, [token, loginInProgress, login]);

  return {
    token,
    error,
    loginInProgress,
    login,
    logout,
  };
}
